import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import classes from './login.module.scss';
import SortpakLogo from '../../../assets/icons/sortpak-logo.png';
import InputField from '../../_common/Form/TextFields/InputField';
import Button from '../../_common/Form/Buttons/Button';
import { getAccountRequest, loginRequest } from '../../../store/actions/auth';
import AccountHelpers from '../../../utils/accountHelpers';
import withPublicRouteCheck from '../../../utils/hoc/withPublicRouteCheck';
import { useQuery } from '../../../utils/hooks/useQuery';
import Loader from '../../_common/Loader/Loader';

const Login = () => {
  const navigate = useNavigate();
  const { query } = useQuery();

  const dispatch = useDispatch();

  const [data, setData] = useState({
    user: '',
    password: '',
  });

  const { user, password } = data;

  const [requiredError, setRequiredError] = useState({
    user: false,
    password: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const getAccount = async ({ token, refreshToken }) => {
    AccountHelpers.setToken(token);
    AccountHelpers.setRefreshToken(refreshToken);

    const userId = AccountHelpers.getDecodedToken()?.id;

    const { payload, status } = await dispatch(getAccountRequest(userId));

    if (status === 'ok') {
      if (AccountHelpers.isLocked()) {
        if (query?.token && query?.refreshToken) {
          throw {
            message: `Account locked due to too many wrong password tries. Try agian after
         ${AccountHelpers.getLockDate().format('YYYY-MM-DD HH:mm:ss')}`,
          };
        } else {
          setError(`Account locked due to too many wrong password tries. Try agian after
         ${AccountHelpers.getLockDate().format('YYYY-MM-DD HH:mm:ss')}`);
        }
      } else if (!AccountHelpers.isActive()) {
        if (query?.token && query?.refreshToken) {
          throw { message: 'This account is not active. Please ask an administrator for more information.' };
        } else {
          setError('This account is not active. Please ask an administrator for more information.');
        }
      } else if (AccountHelpers.isInRole('Physician')) {
        navigate('/patients', { replace: true });
      } else {
        navigate('/scripts', { replace: true });
      }
    } else {
      throw { message: payload?.data?.message };
    }
  };

  useLayoutEffect(() => {
    (async () => {
      if (query?.token && query?.refreshToken) {
        setLoading(true);

        try {
          await getAccount(query);
        } catch (err) {
          AccountHelpers.removeLocalStorage();
          toast.error(err?.message || 'Something went wrong');
        }

        setLoading(false);
      }
    })();
  }, []);

  const changeData = (path, value) => {
    setData((prev) => ({ ...prev, [path]: value }));
    setRequiredError((prev) => ({ ...prev, [path]: false }));
    setError('');
  };

  const submitLogin = async (e) => {
    e.preventDefault();

    if (!user || !password) {
      _.forEach(data, (val, key) => {
        if (!val) setRequiredError((prev) => ({ ...prev, [key]: true }));
      });

      setError('Please fill this fields');

      return;
    }

    setLoading(true);
    const { payload, status } = await dispatch(loginRequest(data));

    if (status === 'ok') {
      await getAccount(payload.data);
    } else {
      const nowUtcString = moment.utc().format('YYYY-MM-DD HH:mm:ss');

      const ACCOUNT_WRONG_PASSWORD_ATTEMPTS = 3;
      const ACCOUNT_LOCKOUT_MINUTES = 30;

      const wrongPasswordCount = payload?.data?.lockedCount || null;

      const punishmentDeadlineUtcString = payload?.data?.lockedDateTimeUTC
        ? moment(payload?.data?.lockedDateTimeUTC).add('m', ACCOUNT_LOCKOUT_MINUTES)
          .format('YYYY-MM-DD HH:mm:ss')
        : null;

      const accountLocked = punishmentDeadlineUtcString
        && moment(punishmentDeadlineUtcString).isAfter(nowUtcString);

      const triesLeft = wrongPasswordCount
        ? `You have ${ACCOUNT_WRONG_PASSWORD_ATTEMPTS - wrongPasswordCount + 1} ${wrongPasswordCount >= 3 ? 'attempt left' : 'attempts left'}`
        : '';

      const message = accountLocked || wrongPasswordCount >= 4
        ? `Account locked due to too many failed login attempts. Account will be unlocked after 
              30 minutes. For any questions, please contact your administrator.`
        : `${payload.data.message} ${triesLeft}`;

      setError(message);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (localStorage.getItem('successMsg')) {
      toast.success(localStorage.getItem('successMsg'));

      localStorage.removeItem('successMsg');
    }
  }, []);

  if (query?.token && query?.refreshToken && loading) {
    return <Loader />;
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.login_block}>
        <img alt="SortPak" src={SortpakLogo} className={classes.icon} />

        <h2 className={classes.title}>Welcome to SortPak</h2>

        <form onSubmit={submitLogin}>
          <div className={classes.inputs_wrapper}>
            <InputField
              label="Username or Email"
              value={user}
              onChange={(value) => changeData('user', value)}
              error={requiredError.user}
            />

            <InputField
              label="Password"
              value={password}
              type="password"
              onChange={(value) => changeData('password', value)}
              error={requiredError.password}
            />

            {error && <div className={classes.error}>{error}</div>}
          </div>

          <Button
            title="Login"
            className={classes.btn}
            type="submit"
            loading={loading}
          />
        </form>
      </div>
    </div>
  );
};

Login.propTypes = {

};

export default withPublicRouteCheck(Login);
